/**
 * Document viewer + export layer.
 *
 * Faithful port of the monolith's full-screen doc-viewer overlay, its
 * share/save/print action helpers, the .txt/.docx save utilities, the plain
 * docx-preview window, and the two juror-print builders. The global `state`
 * and `saveState()` become `store.getState()` / `store.save()`; scoring reads
 * go through `scoringContext()`; toasts are the imported `showToast`.
 *
 * Doc-viewer overlay DOM ids (see src/index.html):
 *   #doc-viewer-overlay  — the overlay container (.open toggles visibility)
 *   #doc-viewer-title    — the topbar title text
 *   #doc-viewer-body     — the rendered document body
 *   #doc-viewer-close    — the close button (onclick="closeDocViewer()")
 */

import type { Juror, Side, ChargeSlug } from '@/types';
import { store, scoringContext } from '@/app/store';
import { scoreJuror, getScoreLabel, getFlags, jurorAge } from '@/core/scoring';
import { generateJurorVdq } from '@/core/analysis';
import { getDetectedCharges, allChargeText } from '@/core/citations';
import { CHARGES } from '@/data/charges';
import { byId } from '@/ui/dom';
import { showToast } from '@/ui/overlays';
import { formatDocHtml } from './format';

// ─── SAVE AS TEXT UTILITY ───
export function saveDocAsText(filename: string, text: string): void {
  // Always force .txt extension
  const fname = filename.replace(/\.[^.]+$/, '') + '.txt';
  const blob = new Blob([text], { type: 'text/plain' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = fname;
  a.click();
  setTimeout(function () { URL.revokeObjectURL(a.href); }, 1000);
  showToast('Saved as ' + fname);
}

export function saveDocAsWord(filename: string, text: string): void {
  const fname = filename.replace(/\.[^.]+$/, '') + '.docx';
  const displayTitle = fname.replace(/\.docx$/, '').replace(/[-_]+/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  const slug = filename.replace(/\.[^.]+$/, '').replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase().slice(0, 40);
  // Show the doc viewer so the user can read, share, email, message, or download
  showPrinterPicker(formatDocHtml(displayTitle, text, true), displayTitle, text, slug);
}

function _doSaveDocAsWord(filename: string, text: string): void {
  // Raw download used by docViewerSaveDocx without re-opening the viewer
  const fname = filename.replace(/\.[^.]+$/, '') + '.docx';
  const displayTitle = fname.replace(/\.docx$/, '').replace(/[-_]+/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  const html = formatDocHtml(displayTitle, text, true);
  const blob = new Blob([html], { type: 'application/vnd.ms-word;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = fname;
  a.click();
  setTimeout(function () { URL.revokeObjectURL(a.href); }, 1000);
  showToast('Saved as ' + fname);
}

// ── Plain docx-preview window ───────────────────────────────────────────────
export function openDocxPreviewWindow(title: string, text: string): void {
  function esc(s: string): string { return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }
  const html = '<!DOCTYPE html><html><head><meta charset="UTF-8"><title>' + esc(title) + '</title>' +
    '<style>body{font-family:"Courier New",monospace;font-size:11pt;color:#111;background:#fff;margin:32px 40px;line-height:1.8;}' +
    'h1{font-family:Arial,Helvetica,sans-serif;font-size:14pt;border-bottom:2px solid #333;padding-bottom:6px;margin-bottom:16px;}' +
    '@media print{body{margin:16px 20px;}}' +
    '</style></head><body>' +
    '<h1>' + esc(title) + '</h1>' +
    '<pre>' + esc(text) + '</pre>' +
    '</body></html>';
  // NOTE: this has the same pop-up exposure as the Law tab's print did —
  // window.open('', '_blank') is blocked by default. It is left as-is because
  // this is a PREVIEW, not a print: routing it through ui/print.ts would fire a
  // print dialog for someone who only asked to look at the document. The right
  // fix is to render it into the doc-viewer overlay, which is a separate change.
  const w = window.open('', '_blank');
  if (w) { w.document.write(html); w.document.close(); }
  else { showToast('Pop-up blocked — check browser settings'); }
}

// ═══════════════════════════════════════════════════════
//  DOC VIEWER — full-screen overlay with share action bar
// ═══════════════════════════════════════════════════════
let _dvTitle = '';
let _dvText = '';
let _dvSlug = '';

export function showPrinterPicker(htmlContent: string, title?: string, plainText?: string, slug?: string): void {
  const bodyMatch = htmlContent.match(/<body[^>]*>([\s\S]*)<\/body>/i);
  const bodyHTML = bodyMatch ? bodyMatch[1] : htmlContent;
  _dvTitle = title || 'Document';
  _dvText = plainText || '';
  _dvSlug = slug || 'document';
  const titleEl = byId('doc-viewer-title');
  if (titleEl) titleEl.textContent = _dvTitle;
  const bodyEl = byId('doc-viewer-body');
  if (bodyEl) { bodyEl.innerHTML = bodyHTML; bodyEl.scrollTop = 0; }
  byId('doc-viewer-overlay')?.classList.add('open');
}

export function closeDocViewer(): void {
  byId('doc-viewer-overlay')?.classList.remove('open');
  const bodyEl = byId('doc-viewer-body');
  if (bodyEl) bodyEl.innerHTML = '';
  _dvText = ''; _dvTitle = ''; _dvSlug = '';
}

export function closePrinterPicker(): void { closeDocViewer(); }

// ── Doc viewer action helpers ──────────────────────────────────────────────
function _dvGetText(): string {
  return _dvText || byId('doc-viewer-body')?.innerText || '';
}
function _dvGetSlug(): string { return _dvSlug || 'document'; }
function _dvGetTitle(): string { return _dvTitle || 'Document'; }

// Build a .docx Blob from the current viewer content
function _dvBuildDocxBlob(): { blob: Blob; fname: string } {
  const slug = _dvGetSlug();
  const fname = slug.replace(/\.[^.]+$/, '') + '.docx';
  const text = _dvGetText();
  const displayTitle = fname.replace(/\.docx$/, '').replace(/[-_]+/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  const html = formatDocHtml(displayTitle, text, true);
  return { blob: new Blob([html], { type: 'application/vnd.ms-word;charset=utf-8' }), fname: fname };
}

// Download .docx directly to device Downloads / Files
export function docViewerDownloadDocx(): void {
  const d = _dvBuildDocxBlob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(d.blob);
  a.download = d.fname;
  a.click();
  setTimeout(function () { URL.revokeObjectURL(a.href); }, 1500);
  showToast('Downloading ' + d.fname);
}

// Save to iOS Files app via Web Share API with file blob
export function docViewerSaveToFiles(): void {
  const d = _dvBuildDocxBlob();
  if (navigator.share && navigator.canShare && navigator.canShare({ files: [new File([d.blob], d.fname, { type: d.blob.type })] })) {
    const file = new File([d.blob], d.fname, { type: d.blob.type });
    navigator.share({ files: [file], title: _dvGetTitle() }).catch(function (err) {
      if (err.name !== 'AbortError') { docViewerDownloadDocx(); }
    });
  } else {
    // Fallback: trigger download
    docViewerDownloadDocx();
  }
}

// AirDrop — same Web Share API with file blob; iOS shows AirDrop as first option
export function docViewerAirDrop(): void {
  const d = _dvBuildDocxBlob();
  if (navigator.share && navigator.canShare) {
    const file = new File([d.blob], d.fname, { type: d.blob.type });
    if (navigator.canShare({ files: [file] })) {
      navigator.share({ files: [file], title: _dvGetTitle() }).catch(function (err) {
        if (err.name !== 'AbortError') { showToast('AirDrop not available — use Share instead'); }
      });
      return;
    }
  }
  showToast('AirDrop not available on this device — use Share');
}

export function docViewerPrint(): void {
  window.print();
}

export function docViewerEmail(): void {
  const subject = encodeURIComponent(_dvGetTitle());
  const body = encodeURIComponent(_dvGetText().slice(0, 2000));
  window.location.href = 'mailto:?subject=' + subject + '&body=' + body;
}

export function docViewerMessage(): void {
  const body = encodeURIComponent(_dvGetText().slice(0, 1600));
  window.location.href = 'sms:?body=' + body;
}

// Add to Notes — opens Apple Notes via share sheet with text
export function docViewerAddToNotes(): void {
  const text = _dvGetText();
  const title = _dvGetTitle();
  // Try Web Share first (shows Notes in sheet on iOS)
  if (navigator.share) {
    navigator.share({ title: title, text: text }).catch(function (err) {
      if (err.name !== 'AbortError') { docViewerCopy(); showToast('Text copied — paste into Notes'); }
    });
  } else {
    // Fallback: copy and prompt
    docViewerCopy();
    showToast('Text copied — paste into Notes');
  }
}

// Add to Reminders — opens system share sheet; user can pick Reminders app
export function docViewerAddReminder(): void {
  const text = _dvGetTitle() + '\n\n' + _dvGetText().slice(0, 500);
  if (navigator.share) {
    navigator.share({ title: _dvGetTitle(), text: text }).catch(function (err) {
      if (err.name !== 'AbortError') {
        docViewerCopy();
        showToast('Text copied — paste into Reminders');
      }
    });
  } else {
    // iOS Reminders URL scheme with title pre-filled
    const url = 'x-apple-reminderkit://REMCDReminder/?title=' + encodeURIComponent(_dvGetTitle().slice(0, 80));
    window.location.href = url;
  }
}

export function docViewerShare(): void {
  const text = _dvGetText();
  if (navigator.share) {
    navigator.share({ title: _dvGetTitle(), text: text }).catch(function () { });
  } else {
    docViewerCopy();
  }
}

export function docViewerCopy(): void {
  const text = _dvGetText();
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text).then(function () { showToast('Copied to clipboard'); }).catch(function () { showToast('Copy not available'); });
  } else {
    showToast('Copy not available on this device');
  }
}

export function docViewerSaveTxt(): void {
  saveDocAsText(_dvGetSlug(), _dvGetText());
}

export function docViewerSaveDocx(): void {
  _doSaveDocAsWord(_dvGetSlug(), _dvGetText());
}

// ═══════════════════════════════════════════════════════
//  JUROR PRINT BUILDERS
// ═══════════════════════════════════════════════════════

/** Case-summary plain text (port of monolith getCaseSummaryText). */
function getCaseSummaryText(): string {
  const s = store.getState();
  const charges = getDetectedCharges(allChargeText(s.chargeList, s.chargeDescription)).map(function (k) { return CHARGES[k] ? CHARGES[k].label : k; }).join(', ') || '(not specified)';
  const atty = s.opp || '(not specified)';
  const defAtty = s.defenseAtty || '(not specified)';
  const elements = (s.crimeElements || []).map(function (e, i) { return '  ' + (i + 1) + '. ' + e; }).join('\n') || (s.chargeDescription ? '  (See charge description: ' + s.chargeDescription + ')' : '  (none entered)');
  const facts = s.indictmentFacts || '(none entered)';
  return 'Case: ' + s.caseName + '\nDocket #: ' + (s.docket || 'N/A') + '\nCourt: ' + s.court + '\nNext Event Date: ' + s.trialDate + '\nJudge: ' + s.judge +
    '\nProsecution/ADA: ' + atty + (s.prossBBO ? ' (BBO# ' + s.prossBBO + ')' : '') + '\nDefense Attorney: ' + defAtty + (s.defBBO ? ' (BBO# ' + s.defBBO + ')' : '') +
    '\nCharges: ' + charges + '\nStatute/Charge Description: ' + (s.chargeDescription || 'N/A') + '\n\nElements of the Charges:\n' + elements + '\n\nFacts from Indictment / Police Report:\n' + facts;
}

/** Jurors sorted by numeric panel id (port of monolith sortedJurorsByPanelId). */
function sortedJurorsByPanelId(): Juror[] {
  const jurors = (store.getState().jurors || []).slice();
  jurors.sort(function (a, b) {
    let an = parseInt(String(a.panelId).replace(/[^0-9]/g, ''), 10);
    let bn = parseInt(String(b.panelId).replace(/[^0-9]/g, ''), 10);
    if (isNaN(an)) an = 0; if (isNaN(bn)) bn = 0;
    return an - bn;
  });
  return jurors;
}

/** Psychological profile prose (port of monolith buildJurorPsychProfile). */
function buildJurorPsychProfile(j: Juror): string {
  const state = store.getState();
  const sc = scoreJuror(j, scoringContext()); const sl = getScoreLabel(sc, state.side);
  const sentences: string[] = [];
  const le = j.le || 'None';
  const isVictim = !!j.victim && j.victim.indexOf('Yes') !== -1;
  const highEdu = (j.edu || '').match(/master|phd|doctoral|jd|law/i);
  const ageMid = jurorAge(j);
  const blueCollar = (j.bizType || '').match(/trades|construct|labor/i) || (j.occ || '').toLowerCase().match(/mechanic|plumb|electr|firefight|construct|contractor/);
  if (le !== 'None' && le !== '') {
    sentences.push('Juror has significant law enforcement ties, which research indicates correlates with pro-prosecution leanings and higher deference to government witnesses (Lieberman &amp; Sales, 2007).');
  }
  if (isVictim) {
    sentences.push('As a reported crime victim, this juror may carry implicit sympathies toward the complaining witness, which voir dire research shows can reduce receptiveness to reasonable-doubt arguments.');
  }
  if (highEdu && (le === 'None' || le === '')) {
    sentences.push('Graduate-educated jurors without law enforcement connections tend to be more analytical, more skeptical of government overreach, and more demanding of the Commonwealth\'s evidentiary burden.');
  }
  if (blueCollar && !isVictim && ageMid < 45) {
    sentences.push('Blue-collar jurors without victimization history tend to understand working-class defendant contexts and may be more open to defense narratives around credibility and identity.');
  }
  if (!sentences.length) {
    sentences.push('This juror presents no strongly predictive psychological indicators based on the information collected; scoring is derived primarily from demographic and occupational baseline factors.');
  }
  const chargeLabel = state.chargeDescription ? state.chargeDescription.split(/\n/)[0].slice(0, 80) : 'the current charges';
  sentences.push('Based on the current charge profile (' + chargeLabel + '), this juror scores ' + sl.label + ' for the ' + (state.side === 'defense' ? 'defense' : 'prosecution') + ' side.');
  return sentences.join(' ');
}

/** For-cause / peremptory challenge analysis HTML (port of monolith buildChallengeAnalysis). */
function buildChallengeAnalysis(j: Juror, side: Side): string {
  const forCause: string[] = [];
  if (j.fair === 'No – stated cannot be fair') {
    forCause.push('Juror stated an inability to be fair, which is grounds for a for-cause challenge. <em>Commonwealth v. Mathews</em>, 406 Mass. 380 (1990) (juror who cannot set aside bias must be excused for cause).');
  }
  if (j.le && j.le !== 'None' && side === 'defense') {
    forCause.push('Juror\'s law enforcement connection may support a for-cause challenge by the defense where impartiality is in doubt. <em>Commonwealth v. Dickerson</em>, 372 Mass. 783 (1977) (close relationship to law enforcement warrants heightened scrutiny during voir dire).');
  }
  if (j.victim && j.victim.indexOf('Yes') !== -1 && side === 'defense') {
    forCause.push('Juror\'s history as a crime victim may support a defense for-cause challenge given potential sympathy toward the Commonwealth\'s witnesses. <em>Cornu v. Commonwealth</em>, 407 Mass. 218 (impartiality concerns from victim experience should be explored fully on the record).');
  }
  if (j.lawEmp === 'Attorney / Lawyer') {
    forCause.push('Juror\'s legal training raises potential impartiality concerns, including the risk that the juror substitutes personal legal knowledge for the court\'s instructions.');
  }
  const sc = scoreJuror(j, scoringContext()); const sl = getScoreLabel(sc, side);
  const favorable = (side === 'defense' && sc >= 2) || (side === 'prosecution' && sc <= -2);
  const peremptory = 'Based on the current score (' + sl.label + '), this juror is ' + (favorable ? 'favorable and should generally be retained' : 'unfavorable and is a reasonable candidate for a peremptory strike') + ' from the ' + side + ' perspective. Any peremptory challenge must be exercised for a race- and gender-neutral reason. <em>Batson v. Kentucky</em>, 476 U.S. 79 (1986); <em>Commonwealth v. Soares</em>, 377 Mass. 461 (1979) (prohibiting peremptory challenges based on group bias).';
  const opposing = 'Opposing counsel may argue that this juror\'s demographic and background factors alone do not establish bias sufficient to justify a challenge, and that any peremptory strike targeting protected characteristics would violate <em>Soares</em>/<em>Batson</em>. Counsel should be prepared to articulate a specific, case-related, non-pretextual basis for any challenge exercised, tied to the juror\'s stated answers rather than group membership.';
  let html = '';
  html += '<div style="margin-bottom:8px;"><div class="dl" style="margin-bottom:3px;">For-Cause Grounds</div>' +
    '<div style="font-size:11px;color:var(--text-secondary);line-height:1.7;">' +
    (forCause.length ? forCause.join('<br><br>') : 'No clear for-cause basis identified from the information collected; a peremptory challenge may still be considered.') +
    '</div></div>';
  html += '<div style="margin-bottom:8px;"><div class="dl" style="margin-bottom:3px;">Peremptory Challenge Recommendation</div>' +
    '<div style="font-size:11px;color:var(--text-secondary);line-height:1.7;">' + peremptory + '</div></div>';
  html += '<div><div class="dl" style="margin-bottom:3px;">Opposing Counsel Arguments</div>' +
    '<div style="font-size:11px;color:var(--text-secondary);line-height:1.7;">' + opposing + '</div></div>';
  return html;
}

export function printSingleJuror(id: number): void {
  const state = store.getState();
  const ctx = scoringContext();
  const side = state.side;
  const activeCharges: ChargeSlug[] = state.activeCharges;
  const j = state.jurors.find(function (x) { return x.id === id; }); if (!j) return;
  const sc = scoreJuror(j, ctx); const sl = getScoreLabel(sc, side); const flags = getFlags(j, ctx);
  const vqs = generateJurorVdq(j, side, activeCharges);
  const caseLine = getCaseSummaryText().split('\n').slice(0, 3).join(' | ');
  const statusLabels: Record<string, string> = { none: 'None', keep: 'Keep', strike: 'Strike', seated: 'Seated', challenged: 'Challenged' };
  const rows: Array<[string, string | number]> = [
    ['Panel ID / Jury #', j.panelId || '—'], ['Seat', j.seat || '—'],
    ['Gender', j.gender || '—'], ['Age Range', j.age || '—'],
    ['Education', j.edu || '—'], ['Employment Status', j.empStatus || '—'],
    ['Business / Industry', j.bizType || '—'], ['Occupation', j.occ || '—'],
    ['Household Size', j.hhSize || '—'], ['Parent / Guardian', j.parent || '—'],
    ['Prior Jury Service', j.jury || '—'], ['LE Connection', j.le || 'None'],
    ['Law-Related Employment', j.lawEmp || 'None'], ['Crime Victim', j.victim || 'No'],
    ['Accused / Family Accused', j.accused || 'No'], ['States Fair', j.fair || '—'],
    ['Case Knowledge', j.caseKnow || '—'], ['Opinion Stated', j.opinion || '—'],
    ['Disposition', statusLabels[j.status || 'none'] || 'None'],
    ['Score (' + side + ')', sl.label]
  ];
  function esc(s: string): string { return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }
  const tableRows = rows.map(function (r) {
    return '<tr><td style="font-weight:600;width:42%;padding:4px 8px;border:1px solid #ccc;background:#f8f8f8;font-size:10px;">' + r[0] + '</td>' +
      '<td style="padding:4px 8px;border:1px solid #ccc;font-size:10px;">' + esc(String(r[1])) + '</td></tr>';
  }).join('');
  const flagsHtml = flags.length
    ? flags.map(function (f) { return '<span style="display:inline-block;background:' + (f.side === 'defense' ? '#dbeafe' : '#fee2e2') + ';color:' + (f.side === 'defense' ? '#1d4ed8' : '#991b1b') + ';border-radius:4px;padding:2px 7px;font-size:9px;font-weight:600;margin:2px;">' + esc(f.label) + '</span>'; }).join('')
    : '<em style="color:#999;font-size:10px;">None</em>';
  const vdHtml = vqs.length
    ? '<ol style="margin:4px 0 0 16px;padding:0;font-size:10px;color:#333;line-height:1.8;">' + vqs.map(function (q) { return '<li>[' + esc(q.cat) + '] ' + esc(q.q) + '</li>'; }).join('') + '</ol>'
    : '<em style="color:#999;font-size:10px;">No charge entered — add statute on Case tab.</em>';
  const notesHtml = j.notes ? '<p style="font-size:10px;color:#333;margin:4px 0 0;white-space:pre-wrap;">' + esc(j.notes) + '</p>' : '<em style="color:#999;font-size:10px;">None</em>';
  const psychHtml = '<p style="font-size:10px;color:#333;line-height:1.7;margin:4px 0 0;">' + buildJurorPsychProfile(j).replace(/&amp;/g, '&') + '</p>';
  const challHtml = buildChallengeAnalysis(j, side)
    .replace(/<div class="dl"[^>]*>/g, '<div style="font-size:10px;font-weight:700;color:#333;margin:6px 0 2px;">')
    .replace(/<div class="dv"[^>]*>/g, '<div style="font-size:10px;color:#444;line-height:1.7;">')
    .replace(/<\/div>/g, '</div>');
  const html = '<!DOCTYPE html><html><head><title>Juror ' + esc(j.panelId) + '</title>' +
    '<style>body{font-family:Arial,Helvetica,sans-serif;font-size:11px;color:#111;margin:24px 28px;}' +
    '@media print{body{margin:16px 20px;}}' +
    '</style></head><body>' +
    '<div style="border-bottom:2px solid #333;margin-bottom:10px;padding-bottom:6px;">' +
    '<div style="font-size:7px;color:#666;text-transform:uppercase;letter-spacing:1px;">SpatchAI Jury Intelligence &nbsp;|&nbsp; ' + esc(caseLine) + '</div>' +
    '<div style="font-size:16px;font-weight:700;color:#111;margin-top:2px;">Juror ' + esc(j.panelId) + ' &nbsp;&mdash;&nbsp; Seat ' + j.seat + '</div>' +
    '</div>' +
    '<table style="width:100%;border-collapse:collapse;margin-bottom:10px;">' + tableRows + '</table>' +
    '<div style="margin-bottom:8px;"><div style="font-size:10px;font-weight:700;color:#333;margin-bottom:3px;">Score Flags</div>' + flagsHtml + '</div>' +
    '<div style="margin-bottom:8px;"><div style="font-size:10px;font-weight:700;color:#333;margin-bottom:2px;">Voir Dire Notes</div>' + notesHtml + '</div>' +
    '<div style="margin-bottom:8px;"><div style="font-size:10px;font-weight:700;color:#333;margin-bottom:2px;">Suggested Voir Dire Questions</div>' + vdHtml + '</div>' +
    '<div style="margin-bottom:8px;"><div style="font-size:10px;font-weight:700;color:#333;margin-bottom:2px;">Psychological Profile</div>' + psychHtml + '</div>' +
    '<div><div style="font-size:10px;font-weight:700;color:#333;margin-bottom:2px;">Challenge Analysis</div>' + challHtml + '</div>' +
    '</body></html>';
  showPrinterPicker(html);
}

export function printAllJurorsFull(): void {
  const state = store.getState();
  const ctx = scoringContext();
  const side = state.side;
  const activeCharges: ChargeSlug[] = state.activeCharges;
  const jurors = sortedJurorsByPanelId();
  if (!jurors.length) { showToast('No jurors on panel to print'); return; }
  const caseLine = getCaseSummaryText().split('\n').slice(0, 3).join(' | ');
  const statusLabels: Record<string, string> = { none: 'None', keep: 'Keep', strike: 'Strike', seated: 'Seated', challenged: 'Challenged' };
  const pages = jurors.map(function (j) {
    const sc = scoreJuror(j, ctx); const sl = getScoreLabel(sc, side); const flags = getFlags(j, ctx);
    const vqs = generateJurorVdq(j, side, activeCharges);
    const rows: Array<[string, string | number]> = [
      ['Panel ID / Jury #', j.panelId || '—'], ['Seat', j.seat || '—'],
      ['Gender', j.gender || '—'], ['Age Range', j.age || '—'],
      ['Education', j.edu || '—'], ['Employment Status', j.empStatus || '—'],
      ['Business / Industry', j.bizType || '—'], ['Occupation', j.occ || '—'],
      ['Household Size', j.hhSize || '—'], ['Parent / Guardian', j.parent || '—'],
      ['Prior Jury Service', j.jury || '—'], ['LE Connection', j.le || 'None'],
      ['Law-Related Employment', j.lawEmp || 'None'], ['Crime Victim', j.victim || 'No'],
      ['Accused / Family Accused', j.accused || 'No'], ['States Fair', j.fair || '—'],
      ['Case Knowledge', j.caseKnow || '—'], ['Opinion Stated', j.opinion || '—'],
      ['Disposition', statusLabels[j.status || 'none'] || 'None'],
      ['Score (' + side + ')', sl.label]
    ];
    const tableRows = rows.map(function (r) {
      return '<tr><td style="font-weight:600;width:42%;padding:4px 8px;border:1px solid #ccc;background:#f8f8f8;font-size:10px;">' + r[0] + '</td>' +
        '<td style="padding:4px 8px;border:1px solid #ccc;font-size:10px;">' + escHtml(String(r[1])) + '</td></tr>';
    }).join('');
    const flagsHtml = flags.length
      ? flags.map(function (f) { return '<span style="display:inline-block;background:' + (f.side === 'defense' ? '#dbeafe' : '#fee2e2') + ';color:' + (f.side === 'defense' ? '#1d4ed8' : '#991b1b') + ';border-radius:4px;padding:2px 7px;font-size:9px;font-weight:600;margin:2px;">' + escHtml(f.label) + '</span>'; }).join('')
      : '<em style="color:#999;font-size:10px;">None</em>';
    const vdHtml = vqs.length
      ? '<ol style="margin:4px 0 0 16px;padding:0;font-size:10px;color:#333;line-height:1.8;">' + vqs.map(function (q) { return '<li>[' + escHtml(q.cat) + '] ' + escHtml(q.q) + '</li>'; }).join('') + '</ol>'
      : '<em style="color:#999;font-size:10px;">No charge entered — add statute on Case tab.</em>';
    const notesHtml = j.notes ? '<p style="font-size:10px;color:#333;margin:4px 0 0;white-space:pre-wrap;">' + escHtml(j.notes) + '</p>' : '<em style="color:#999;font-size:10px;">None</em>';
    const psychHtml = '<p style="font-size:10px;color:#333;line-height:1.7;margin:4px 0 0;">' + buildJurorPsychProfile(j).replace(/&amp;/g, '&') + '</p>';
    const challHtml = buildChallengeAnalysis(j, side)
      .replace(/<div class="dl"[^>]*>/g, '<div style="font-size:10px;font-weight:700;color:#333;margin:6px 0 2px;">')
      .replace(/<div class="dv"[^>]*>/g, '<div style="font-size:10px;color:#444;line-height:1.7;">')
      .replace(/<\/div>/g, '</div>');
    return '<div class="juror-page">' +
      '<div style="border-bottom:2px solid #333;margin-bottom:10px;padding-bottom:6px;">' +
      '<div style="font-size:7px;color:#666;text-transform:uppercase;letter-spacing:1px;">SpatchAI Jury Intelligence &nbsp;|&nbsp; ' + escHtml(caseLine) + '</div>' +
      '<div style="font-size:16px;font-weight:700;color:#111;margin-top:2px;">Juror ' + escHtml(j.panelId) + ' &nbsp;&mdash;&nbsp; Seat ' + j.seat + '</div>' +
      '</div>' +
      '<table style="width:100%;border-collapse:collapse;margin-bottom:10px;">' + tableRows + '</table>' +
      '<div style="margin-bottom:8px;"><div style="font-size:10px;font-weight:700;color:#333;margin-bottom:3px;">Score Flags</div>' + flagsHtml + '</div>' +
      '<div style="margin-bottom:8px;"><div style="font-size:10px;font-weight:700;color:#333;margin-bottom:2px;">Voir Dire Notes</div>' + notesHtml + '</div>' +
      '<div style="margin-bottom:8px;"><div style="font-size:10px;font-weight:700;color:#333;margin-bottom:2px;">Suggested Voir Dire Questions</div>' + vdHtml + '</div>' +
      '<div style="margin-bottom:8px;"><div style="font-size:10px;font-weight:700;color:#333;margin-bottom:2px;">Psychological Profile</div>' + psychHtml + '</div>' +
      '<div><div style="font-size:10px;font-weight:700;color:#333;margin-bottom:2px;">Challenge Analysis</div>' + challHtml + '</div>' +
      '</div>';
  });
  function escHtml(s: string): string { return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }
  const html = '<!DOCTYPE html><html><head><title>All Juror Information</title>' +
    '<style>' +
    'body{font-family:Arial,Helvetica,sans-serif;font-size:11px;color:#111;margin:0;padding:0;}' +
    '.juror-page{padding:24px 28px;box-sizing:border-box;}' +
    '@media print{.juror-page{page-break-after:always;padding:16px 20px;}body{margin:0;}}' +
    '</style></head><body>' + pages.join('') + '</body></html>';
  showPrinterPicker(html);
}
